/**
 * Canvas side of uploaded backgrounds: a chosen photo is resized to the
 * published camera resolution (the same 1280x720 the static WebP backgrounds
 * use) and re-encoded, so a 10+ MP phone photo costs the same per-frame work
 * as a bundled one. A separate tiny thumbnail feeds the picker tile.
 */

/** Matches the published camera resolution (VideoPresets.h720). */
export const FULL_MAX_WIDTH = 1280;
export const FULL_MAX_HEIGHT = 720;

/** Matches the bundled *-thumb.webp tiles. */
export const THUMB_MAX_WIDTH = 320;
export const THUMB_MAX_HEIGHT = 200;

/**
 * Sources above these limits are rejected from their headers, before any
 * decoding: the picture is scaled down to 1280x720 anyway, and a small file
 * can declare huge pixel dimensions (20 MB is enough for a 30000x30000 PNG)
 * whose decode exhausts the tab's memory or locks it for seconds.
 */
export const MAX_SOURCE_DIMENSION = 8192;
export const MAX_SOURCE_PIXELS = 32_000_000;
/** How much of the file is probed for the dimensions: a JPEG can hide its
 * frame header behind large EXIF segments. */
const DIMENSION_PROBE_BYTES = 1024 * 1024;

const WEBP_MIME = 'image/webp';
const JPEG_MIME = 'image/jpeg';
const ENCODE_QUALITY = 0.9;

export type ProcessedImage = { full: Blob; thumb: Blob };

/** Thrown when the upload declares more pixels than we are willing to decode. */
export class ImageResolutionError extends Error {
  constructor() {
    super('Image resolution exceeds the allowed maximum');
    this.name = 'ImageResolutionError';
  }
}

/**
 * Scale that makes the source cover the target box completely. The canvas ends
 * up exactly targetWidth x targetHeight — portraits and small images are
 * cropped to fill it instead of being stored off-size — with the overflow cut
 * from both sides.
 */
export function coverScale(
  width: number,
  height: number,
  targetWidth: number,
  targetHeight: number,
): number {
  return Math.max(targetWidth / width, targetHeight / height);
}

/**
 * Reads the intrinsic pixel size of PNG, JPEG or WebP straight from the file
 * header, without decoding the image. Undefined means the format is not one of
 * those, the header is truncated or the walk did not settle — such files fall
 * through to the browser's own decode limits.
 */
export function readImageDimensions(
  bytes: Uint8Array,
): { width: number; height: number } | undefined {
  const ascii = (offset: number, text: string) => {
    if (offset + text.length > bytes.length) {
      return false;
    }
    for (let index = 0; index < text.length; index += 1) {
      if (bytes[offset + index] !== text.charCodeAt(index)) {
        return false;
      }
    }
    return true;
  };

  // PNG: the mandatory IHDR chunk carries the size right after the signature.
  if (ascii(0, '\x89PNG\r\n\x1a\n')) {
    if (bytes.length < 24 || !ascii(12, 'IHDR')) {
      return undefined;
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return { width: view.getUint32(16), height: view.getUint32(20) };
  }

  // JPEG: walk the segment chain until the frame header (SOFn).
  if (bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    let position = 2;
    while (position + 4 <= bytes.length && bytes[position] === 0xff) {
      const marker = bytes[position + 1];
      if (marker === 0xff) {
        position += 1; // fill byte before a marker
        continue;
      }
      if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
        position += 2; // standalone markers carry no length
        continue;
      }
      if (marker === 0xda || marker === 0xd9 || marker === 0xd8) {
        return undefined; // scan data or end of image: no frame header seen
      }
      const length = (bytes[position + 2] << 8) | bytes[position + 3];
      const isFrameHeader =
        marker >= 0xc0 &&
        marker <= 0xcf &&
        marker !== 0xc4 && // DHT
        marker !== 0xc8 && // JPG
        marker !== 0xcc; // DAC
      if (isFrameHeader) {
        if (position + 9 > bytes.length) {
          return undefined;
        }
        return {
          height: (bytes[position + 5] << 8) | bytes[position + 6],
          width: (bytes[position + 7] << 8) | bytes[position + 8],
        };
      }
      position += 2 + length;
    }
    return undefined;
  }

  // WebP: a RIFF container; the chunk name picks the size layout.
  if (ascii(0, 'RIFF') && ascii(8, 'WEBP')) {
    if (ascii(12, 'VP8X')) {
      if (bytes.length < 30) {
        return undefined;
      }
      return {
        width: 1 + bytes[24] + (bytes[25] << 8) + (bytes[26] << 16),
        height: 1 + bytes[27] + (bytes[28] << 8) + (bytes[29] << 16),
      };
    }
    if (ascii(12, 'VP8 ')) {
      if (bytes.length < 30 || bytes[23] !== 0x9d || bytes[24] !== 0x01 || bytes[25] !== 0x2a) {
        return undefined;
      }
      return {
        width: (bytes[26] | (bytes[27] << 8)) & 0x3fff,
        height: (bytes[28] | (bytes[29] << 8)) & 0x3fff,
      };
    }
    if (ascii(12, 'VP8L')) {
      if (bytes.length < 25 || bytes[20] !== 0x2f) {
        return undefined;
      }
      const bits = bytes[21] | (bytes[22] << 8) | (bytes[23] << 16) | (bytes[24] << 24);
      return { width: (bits & 0x3fff) + 1, height: ((bits >> 14) & 0x3fff) + 1 };
    }
  }

  return undefined;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      // Browsers that cannot encode the requested type fall back to PNG per
      // spec, so a returned blob with a different type means "try another".
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas encoding failed'))),
      type,
      ENCODE_QUALITY,
    );
  });
}

async function encode(canvas: HTMLCanvasElement): Promise<Blob> {
  const webp = await canvasToBlob(canvas, WEBP_MIME);
  if (webp.type === WEBP_MIME) {
    return webp;
  }
  return canvasToBlob(canvas, JPEG_MIME);
}

type DecodedImage = ImageBitmap | HTMLImageElement;

async function decode(blob: Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      // Phone photos carry an EXIF rotation; without this flag some browsers
      // draw them sideways.
      return await createImageBitmap(blob, { imageOrientation: 'from-image' });
    } catch {
      return createImageBitmap(blob);
    }
  }
  // Same requirement the track processor has, but decoding here must not be
  // the thing that breaks the upload on an old browser.
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Image decode failed'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function sourceSize(image: DecodedImage) {
  if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {
    return { width: image.width, height: image.height };
  }
  return {
    width: (image as HTMLImageElement).naturalWidth,
    height: (image as HTMLImageElement).naturalHeight,
  };
}

function renderToBlob(
  source: DecodedImage,
  targetWidth: number,
  targetHeight: number,
): Promise<Blob> {
  const original = sourceSize(source);
  const scale = coverScale(original.width, original.height, targetWidth, targetHeight);
  const drawnWidth = Math.round(original.width * scale);
  const drawnHeight = Math.round(original.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D context is unavailable');
  }
  // Center the scaled image: equal parts are cropped off both sides.
  context.drawImage(
    source as CanvasImageSource,
    Math.round((targetWidth - drawnWidth) / 2),
    Math.round((targetHeight - drawnHeight) / 2),
    drawnWidth,
    drawnHeight,
  );
  return encode(canvas);
}

/** Resizes the upload to the camera resolution plus a thumbnail for the tile. */
export async function processImageFile(file: Blob): Promise<ProcessedImage> {
  // The dimensions come from the header when it can be read: a small file can
  // declare huge ones, and the decode — not the drawing — is what runs out of
  // memory.
  const probe = new Uint8Array(await file.slice(0, DIMENSION_PROBE_BYTES).arrayBuffer());
  const dimensions = readImageDimensions(probe);
  if (
    dimensions &&
    (dimensions.width > MAX_SOURCE_DIMENSION ||
      dimensions.height > MAX_SOURCE_DIMENSION ||
      dimensions.width * dimensions.height > MAX_SOURCE_PIXELS)
  ) {
    throw new ImageResolutionError();
  }
  const image = await decode(file);
  try {
    const [full, thumb] = await Promise.all([
      renderToBlob(image, FULL_MAX_WIDTH, FULL_MAX_HEIGHT),
      renderToBlob(image, THUMB_MAX_WIDTH, THUMB_MAX_HEIGHT),
    ]);
    return { full, thumb };
  } finally {
    if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {
      image.close();
    }
  }
}
